// Lo que el shell muestra según la ruta, con las mismas reglas que PortalLayout.razor del portal actual. Los permisos ya
// vienen decididos por el servidor (GET /api/portal/shell); aquí solo se arma la presentación de esa ruta.

export type ShellData = {
  homeEnabled: boolean;
  settingsEnabled: boolean;
  chatEnabled: boolean;
  contactsEnabled: boolean;
  employeesEnabled: boolean;
  erpEnabled: boolean;
  crmEnabled: boolean;
  crmPermissions: string[];
  isTeamAdmin: boolean;
  isSupport: boolean;
  supportOrganization: string | null;
  supportSubject: string | null;
  profileName: string;
  profileInitials: string;
  organizationName: string;
  companies: { id: number; name: string; isActive: boolean }[];
  pendingApprovals: number | null;
  reportScope: string;
  /** IDN-7: la empresa está inactiva; el portal muestra el aviso en vez del shell. */
  organizationInactive?: boolean;
};

export type ContextItem = { label: string; href: string; isActive: boolean };
export type Label = (key: string) => string;

/** Claves de texto del shell (las mismas que pide PortalLayout y PortalHeader). */
export const shellTextKeys = [
  "A11Y_SKIP",
  "ORG_SWITCH_LABEL",
  "SHELL_CONTEXT_NAVIGATION",
  "SHELL_CONTEXT_SECTIONS",
  "SHELL_CLOSE",
  "NAV_MENU",
  "CP_SHELL_PRIMARY_NAV",
  "NAV_ERP",
  "ERP_DASHBOARD",
  "ERP_PARTNERS",
  "ERP_ORDERS",
  "ERP_PURCHASES",
  "ERP_INVENTORY",
  "ERP_RECEIVABLES",
  "ERP_PAYABLES",
  "ERP_CASH",
  "ERP_EXPENSES",
  "ERP_REPORTS",
  "CRM_TITLE",
  "CRM_CONTACTS",
  "CRM_LEADS",
  "CRM_OPP_WORK",
  "CRM_OPPORTUNITIES",
  "CRM_OFFERS",
  "CRM_SALES",
  "CRM_LEAD_ACTIVITIES",
  "CRM_ANALYTICS_TITLE",
  "CRM_LEAD_CONFIG",
  "CRM_LEAD_QUALIFICATION",
  "CRM_ACCOUNTS",
  "NAV_HOME",
  "NAV_EMPLOYEES",
  "SIM_GLOBAL_TITLE",
  "SIM_R1_OPEN",
  "SIM_ACTIVITY_ONE",
  "SIM_ACTIVITY_COUNT",
  "SIM_R1_SAFE_NOTICE",
  "NAV_KNOWLEDGE",
  "NAV_CHATS",
  "NAV_APPROVALS",
  "SHELL_PENDING_COUNT",
  "NAV_INBOXES",
  "NAV_CHANNELS",
  "NAV_CHAT",
  "REPLY_TITLE",
  "SHELL_CHAT_CONVERSATIONS",
  "CHATCFG_TITLE",
  "E1_SETTINGS",
  "STAT_E1_TITLE",
  "NAV_OPERATION",
  "NAV_SETTINGS",
  "NAV_PROFILE",
  "NAV_PREFS",
  "SEC_TITLE",
  "NAV_ACCOUNT",
  "ACCOUNT_SIGN_OUT",
  "CP_SHELL_MENU_COLLAPSE",
  "CP_SHELL_MENU_EXPAND",
  "SETTINGS_COMPANY",
  "TEAM_TITLE",
  "USERS_TITLE",
  "CFG1_TEAM",
  "SETTINGS_SCHEDULE",
  "SETTINGS_MODULES",
  "SUPPORT_BANNER",
  "SUPPORT_BANNER_VIEWING",
  "SUPPORT_BANNER_END",
  "SHELL_BRAND_HOME",
  "SHELL_COMPANY_NAME",
  "ORG_SWITCH_TITLE",
  "CP_SUPPORT_SCOPE_FIXED",
  "SHELL_COMPANY_SETTINGS",
  "SHELL_QUICK_TOOLS",
  "SHELL_NAV_MORE",
  "SHELL_SWITCH_COMPANY",
];

const lower = (value: string) => value.toLowerCase();
const starts = (path: string, prefix: string) => lower(path).startsWith(prefix);

/** Sección global de la ruta (PortalSection.Of). */
export function sectionOf(path: string): string {
  const segments = path
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean);
  if (segments.length === 0 || lower(segments[0]!) !== "portal") return "";
  if (segments.length === 1) return "home";
  switch (lower(segments[1]!)) {
    case "chat":
    case "chats":
      return "chat";
    case "empleados":
      return "empleados";
    case "conocimiento":
      return "conocimiento";
    case "aprobaciones":
      return "aprobaciones";
    case "bandejas":
      return "bandejas";
    case "operacion":
      return "operacion";
    case "canales":
      return "canales";
    case "configuracion":
      return "configuracion";
    case "perfil":
    case "preferencias":
    case "seguridad":
    case "organizaciones":
      return "home";
    default:
      return "";
  }
}

/** Banderas de la ruta actual (mismos nombres que en PortalLayout). */
export function routeFlags(rawPath: string, search: string) {
  const path = rawPath.replace(/\/+$/, "") || "";
  const isDedicatedChat = ["/portal/chat", "/portal/chat/index"].includes(lower(path));
  const isContacts = starts(path, "/portal/chat/contactos");
  const isInternalChats = lower(path) === "/portal/chats" || starts(path, "/portal/chats/");
  const isChatGroupPolicy = isInternalChats && lower(search).includes("config=groups");
  const isChatConfiguration =
    starts(path, "/portal/chat/configuracion") ||
    starts(path, "/portal/configuracion/chat") ||
    starts(path, "/portal/bandejas") ||
    starts(path, "/portal/canales") ||
    isChatGroupPolicy;
  const isReplies = starts(path, "/portal/chat/respuestas");
  const isChatTeam = starts(path, "/portal/chat/equipo");
  const isStatistics = starts(path, "/portal/chat/estadisticas");
  const isChatSection =
    isDedicatedChat || isContacts || isChatConfiguration || isReplies || isInternalChats || isChatTeam || isStatistics;
  const isSimulator =
    lower(path) === "/portal/empleados/probar" ||
    (starts(path, "/portal/empleados/") && lower(path).endsWith("/probar"));
  const isErpSection = lower(path) === "/erp" || starts(path, "/erp/");
  const isCrmSection = lower(path) === "/crm" || starts(path, "/crm/");
  const section = sectionOf(path);
  const isConfigSection = ["configuracion", "bandejas", "canales"].includes(section);
  const isModuleSection = [
    "chat",
    "empleados",
    "conocimiento",
    "chats",
    "aprobaciones",
    "bandejas",
    "operacion",
    "canales",
  ].includes(section);
  return {
    path,
    isDedicatedChat,
    isContacts,
    isInternalChats,
    isChatGroupPolicy,
    isChatConfiguration,
    isReplies,
    isChatTeam,
    isStatistics,
    isChatSection,
    isSimulator,
    isErpSection,
    isCrmSection,
    section,
    isConfigSection,
    isModuleSection,
  };
}

export type RouteFlags = ReturnType<typeof routeFlags>;

function crmItems(data: ShellData, flags: RouteFlags, L: Label): ContextItem[] {
  const has = (code: string) => data.crmPermissions.includes(code);
  const items: ContextItem[] = [];
  const add = (allowed: boolean, label: string, path: string) => {
    if (allowed) items.push({ label, href: path, isActive: starts(flags.path, lower(path.split("?")[0]!)) });
  };
  const work = has("crm.opportunity.read") || has("crm.activity.read") || has("crm.lead.read");
  add(work, L("CRM_OPP_WORK"), "/crm/trabajo");
  const analytics = work || has("crm.sale.read");
  add(
    analytics || has("crm.offer.read") || has("crm.contact.read"),
    L("CRM_ANALYTICS_TITLE"),
    analytics
      ? "/crm/analytics"
      : has("crm.offer.read")
        ? "/crm/analytics?section=ofertas"
        : "/crm/analytics?section=calidad",
  );
  add(has("crm.opportunity.read"), L("CRM_OPPORTUNITIES"), "/crm/oportunidades");
  add(has("crm.offer.read"), L("CRM_OFFERS"), "/crm/ofertas");
  add(has("crm.sale.read"), L("CRM_SALES"), "/crm/ventas");
  add(has("crm.activity.read"), L("CRM_LEAD_ACTIVITIES"), "/crm/actividades");
  add(has("crm.contact.read"), L("CRM_CONTACTS"), "/crm/contactos");
  add(has("crm.account.read"), L("CRM_ACCOUNTS"), "/crm/empresas");
  add(has("crm.lead.read"), L("CRM_LEADS"), "/crm/leads");
  const general = has("crm.lead.read") || has("crm.lead.create") || has("crm.config.manage");
  add(
    general || has("crm.pipeline.read"),
    L("CRM_LEAD_CONFIG"),
    general ? "/crm/configuracion" : "/crm/configuracion/pipelines",
  );
  return items;
}

/** Entrada al CRM: el trabajo si lo puede usar; si no, su primera sección. */
export function crmEntryPath(data: ShellData, flags: RouteFlags, L: Label): string {
  const has = (code: string) => data.crmPermissions.includes(code);
  return has("crm.opportunity.read") || has("crm.activity.read") || has("crm.lead.read")
    ? "/crm"
    : (crmItems(data, flags, L)[0]?.href ?? "/portal");
}

/** Secciones del contexto de la ruta (las de ChatContextItems, ErpContextItems, ConfigContextItems, …). */
export function contextItems(data: ShellData, flags: RouteFlags, L: Label): ContextItem[] {
  const p = flags.path;
  if (flags.isChatSection && (data.chatEnabled || data.contactsEnabled)) {
    // Orden del dueño (2026-10-27): Conversaciones, Contactos, Respuestas, Equipo, Estadísticas y Configuración.
    const items: ContextItem[] = [];
    if (data.chatEnabled)
      items.push({
        label: L("SHELL_CHAT_CONVERSATIONS"),
        href: "/portal/chat",
        isActive: flags.isDedicatedChat || (flags.isInternalChats && !flags.isChatGroupPolicy),
      });
    if (data.contactsEnabled)
      items.push({ label: L("CRM_CONTACTS"), href: "/portal/chat/contactos", isActive: flags.isContacts });
    if (data.chatEnabled)
      items.push({ label: L("REPLY_TITLE"), href: "/portal/chat/respuestas", isActive: flags.isReplies });
    if (data.chatEnabled && data.isTeamAdmin && !data.isSupport)
      items.push({ label: L("CFG1_TEAM"), href: "/portal/chat/equipo", isActive: flags.isChatTeam });
    if (data.chatEnabled)
      items.push({ label: L("STAT_E1_TITLE"), href: "/portal/chat/estadisticas", isActive: flags.isStatistics });
    if (data.chatEnabled)
      items.push({ label: L("E1_SETTINGS"), href: "/portal/chat/configuracion", isActive: flags.isChatConfiguration });
    return items;
  }
  if (data.erpEnabled && flags.isErpSection) {
    return (
      [
        ["ERP_DASHBOARD", "/erp"],
        ["ERP_PARTNERS", "/erp/socios"],
        ["ERP_ORDERS", "/erp/pedidos"],
        ["ERP_PURCHASES", "/erp/compras"],
        ["ERP_INVENTORY", "/erp/inventario"],
        ["ERP_RECEIVABLES", "/erp/cobrar"],
        ["ERP_PAYABLES", "/erp/pagar"],
        ["ERP_CASH", "/erp/caja"],
        ["ERP_EXPENSES", "/erp/gastos"],
        ["ERP_REPORTS", "/erp/reportes"],
      ] as const
    ).map(([key, href]) => ({ label: L(key), href, isActive: href === "/erp" ? p === "/erp" : starts(p, href) }));
  }
  if (flags.isConfigSection) {
    const path = lower(p);
    const items: ContextItem[] = [
      { label: L("SETTINGS_COMPANY"), href: "/portal/configuracion/empresa", isActive: path.includes("/empresa") },
    ];
    if (data.isTeamAdmin)
      items.push({
        label: L("USERS_TITLE"),
        href: "/portal/configuracion/usuarios",
        isActive: path.includes("/usuarios") || path.includes("/equipo"),
      });
    items.push({
      label: L("CHATCFG_TITLE"),
      href: "/portal/configuracion/chat",
      isActive: path.includes("/configuracion/chat"),
    });
    if (data.employeesEnabled) {
      items.push({ label: L("NAV_INBOXES"), href: "/portal/bandejas", isActive: flags.section === "bandejas" });
      items.push({ label: L("NAV_CHANNELS"), href: "/portal/canales", isActive: flags.section === "canales" });
    }
    // Accesos C: Horarios vive en Chat › Configuración › Horarios y disponibilidad; lo ve quien lo puede abrir.
    if (data.isTeamAdmin)
      items.push({ label: L("SETTINGS_SCHEDULE"), href: "/portal/chat/configuracion/horarios", isActive: false });
    items.push({
      label: L("SETTINGS_MODULES"),
      href: "/portal/configuracion/modulos",
      isActive: path.includes("/modulos"),
    });
    return items;
  }
  if (data.crmEnabled && flags.isCrmSection) return crmItems(data, flags, L);
  if (data.employeesEnabled && flags.isModuleSection) {
    const s = flags.section;
    return [
      { label: L("NAV_CHAT"), href: "/portal/chat", isActive: s === "chat" },
      { label: L("NAV_EMPLOYEES"), href: "/portal/empleados", isActive: s === "empleados" },
      { label: L("NAV_KNOWLEDGE"), href: "/portal/conocimiento", isActive: s === "conocimiento" },
      { label: L("NAV_APPROVALS"), href: "/portal/aprobaciones", isActive: s === "aprobaciones" },
      { label: L("NAV_INBOXES"), href: "/portal/bandejas", isActive: s === "bandejas" },
      { label: L("NAV_OPERATION"), href: "/portal/operacion", isActive: s === "operacion" },
      { label: L("NAV_CHANNELS"), href: "/portal/canales", isActive: s === "canales" },
    ];
  }
  return [];
}

/** Título e ícono del contexto en el encabezado. */
export function headerContext(data: ShellData, flags: RouteFlags, L: Label): { title: string; icon: string } {
  const icon = flags.isChatSection
    ? "message-square-text"
    : flags.isErpSection
      ? "package"
      : flags.isCrmSection
        ? "contact"
        : flags.isConfigSection
          ? "settings"
          : flags.isModuleSection
            ? "user-cog"
            : "house";
  let title: string;
  if (flags.isErpSection) title = L("NAV_ERP");
  else if (flags.isChatSection) title = L("NAV_CHAT");
  else if (flags.isConfigSection) title = L("NAV_SETTINGS");
  else if (data.crmEnabled && flags.isCrmSection) title = L("CRM_TITLE");
  else if (data.employeesEnabled && flags.isModuleSection) title = L("NAV_EMPLOYEES");
  else
    title =
      (
        {
          "/portal/perfil": L("NAV_PROFILE"),
          "/portal/preferencias": L("NAV_PREFS"),
          "/portal/seguridad": L("SEC_TITLE"),
          "/portal/organizaciones": L("ORG_SWITCH_LABEL"),
          "/portal/simulacion": L("SIM_GLOBAL_TITLE"),
        } as Record<string, string>
      )[lower(flags.path)] ?? L("NAV_HOME");
  return { title, icon };
}

/** Destinos de la navegación global efectivos: con uno solo no se muestra el menú global. */
export function showGlobalNavigation(data: ShellData): boolean {
  const count =
    (data.homeEnabled ? 1 : 0) +
    (data.settingsEnabled ? 1 : 0) +
    (data.chatEnabled || data.contactsEnabled ? 1 : 0) +
    (data.employeesEnabled ? 4 : 0) +
    (data.crmEnabled ? 1 : 0) +
    (data.erpEnabled ? 1 : 0);
  return count > 1;
}

/** Inicio del portal según lo que la persona puede usar. */
export function homePath(data: ShellData, flags: RouteFlags, L: Label): string {
  return data.homeEnabled
    ? "/portal"
    : data.chatEnabled
      ? "/portal/chat"
      : data.contactsEnabled
        ? "/portal/chat/contactos"
        : data.erpEnabled
          ? "/erp"
          : data.crmEnabled
            ? crmEntryPath(data, flags, L)
            : "/portal/perfil";
}

/** Clase del enlace global (Section de PortalLayout). */
export function navClass(key: string, flags: RouteFlags): string {
  if (flags.isChatSection) return key === "chat" ? "nav-link active" : "nav-link";
  return (key === "configuracion" && flags.isConfigSection) || flags.section === key ? "nav-link active" : "nav-link";
}

/** Badge de pendientes (99+). */
export const badge = (count: number) => (count > 99 ? "99+" : String(count));

/** Iniciales de un nombre (ShellPresentation.Initials con solo nombre). */
export function initials(first: string | null | undefined, last?: string | null, email?: string | null): string {
  let a = first?.trim() ?? "";
  let b = last?.trim() ?? "";
  if (!a) {
    a = email?.trim() ?? "";
    b = "";
  }
  const chars = [...a.toUpperCase()];
  let result = chars[0] ?? "";
  if (b) result += [...b.toUpperCase()][0] ?? "";
  else if (chars.length > 1) result += chars[1];
  return result;
}

/** Ícono de cada destino de contexto (HeaderContextNav.IconFor). */
export function iconFor(href: string): string {
  const path = lower(href.split(/[?#]/)[0]!.replace(/\/+$/, ""));
  const rules: [(p: string) => boolean, string][] = [
    [(p) => p.startsWith("/crm/trabajo"), "briefcase-business"],
    [(p) => p.startsWith("/crm/analytics") || p.startsWith("/erp/reportes"), "chart-no-axes-combined"],
    [(p) => p.startsWith("/crm/oportunidades"), "target"],
    [(p) => p.startsWith("/crm/ofertas"), "file-text"],
    [(p) => p.startsWith("/crm/ventas"), "receipt"],
    [(p) => p.startsWith("/crm/actividades"), "calendar-check"],
    [(p) => p.startsWith("/crm/contactos"), "contact"],
    [(p) => p.startsWith("/erp/socios"), "users"],
    [(p) => p.startsWith("/crm/empresas"), "building-2"],
    [(p) => p.startsWith("/crm/leads"), "user-round-plus"],
    [(p) => p.startsWith("/erp/pedidos"), "shopping-bag"],
    [(p) => p.startsWith("/erp/compras"), "shopping-cart"],
    [(p) => p.startsWith("/erp/cobrar"), "wallet"],
    [(p) => p.startsWith("/erp/pagar"), "credit-card"],
    [(p) => p.startsWith("/erp/caja"), "banknote"],
    [(p) => p.startsWith("/erp/gastos"), "receipt-text"],
    [(p) => p === "/portal/chat" || p === "/portal/chat/index", "messages-square"],
    [(p) => p.startsWith("/portal/chat/respuestas"), "zap"],
    [(p) => p.startsWith("/portal/chat/equipo"), "users"],
    [(p) => p.startsWith("/portal/chat/estadisticas"), "chart-no-axes-combined"],
    [(p) => p.includes("contact"), "contact"],
    [(p) => p.includes("configura"), "settings"],
    [(p) => p.includes("conocimiento"), "book-open"],
    [(p) => p.includes("empleados"), "user-cog"],
    [(p) => p.includes("aprobacion"), "list-checks"],
    [(p) => p.includes("socios") || p.includes("cuentas"), "building-2"],
    [(p) => p.includes("pedidos") || p.includes("compras") || p.includes("inventario"), "package"],
  ];
  return rules.find(([match]) => match(path))?.[1] ?? "layout-dashboard";
}
